/**
 * Home page with dashboard overview.
 */

import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { api } from '@/lib/api'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import {
  MessageCircle,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react'
import { miniAppQueryKey, useMiniAppQueryScope } from '@/lib/query-scope'

interface DashboardStats {
  leads: {
    total: number
    new: number
    trend: number
  }
  deals: {
    total: number
    value: number
    trend: number
  }
  agents: {
    active: number
    tasksToday: number
    escalations: number
  }
  approvals: {
    pending: number
    todayApproved: number
  }
}

export function HomePage() {
  const { user, tenant } = useAuthStore()
  const scope = useMiniAppQueryScope()

  const { data: stats, isLoading } = useQuery({
    queryKey: miniAppQueryKey(scope, 'dashboard-stats'),
    queryFn: () => api.get<DashboardStats>('/stats/dashboard'),
    enabled: Boolean(scope),
  })

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl font-bold text-tg-text">
          {getGreeting()}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-tg-hint text-sm">
          {tenant?.name}
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          icon={<MessageCircle className="w-5 h-5" />}
          label="Активных диалогов"
          value={stats?.agents.active ?? 0}
          isLoading={isLoading}
        />
        <StatCard
          icon={<CheckCircle className="w-5 h-5" />}
          label="Ожидают одобрения"
          value={stats?.approvals.pending ?? 0}
          isLoading={isLoading}
          highlight={stats?.approvals.pending ? stats.approvals.pending > 0 : false}
        />
        <StatCard
          icon={<TrendingUp className="w-5 h-5" />}
          label="Лидов сегодня"
          value={stats?.leads.new ?? 0}
          isLoading={isLoading}
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Эскалаций"
          value={stats?.agents.escalations ?? 0}
          isLoading={isLoading}
          highlight={stats?.agents.escalations ? stats.agents.escalations > 0 : false}
        />
      </div>

      {/* Quick actions */}
      <Card className="p-4">
        <h2 className="font-semibold text-tg-text mb-3">Быстрые действия</h2>
        <div className="grid grid-cols-2 gap-2">
          <QuickAction
            icon={<CheckCircle className="w-4 h-4" />}
            label="Одобрения"
            count={stats?.approvals.pending}
            href="/approvals"
          />
          <QuickAction
            icon={<MessageCircle className="w-4 h-4" />}
            label="Диалоги"
            count={stats?.agents.active}
            href="/dialogs"
          />
        </div>
      </Card>
    </div>
  )
}
function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 6) return 'Доброй ночи'
  if (hour < 12) return 'Доброе утро'
  if (hour < 18) return 'Добрый день'
  return 'Добрый вечер'
}

interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: number
  isLoading: boolean
  highlight?: boolean
}

function StatCard({ icon, label, value, isLoading, highlight }: StatCardProps) {
  return (
    <Card className={`p-3 ${highlight ? 'border-tg-button border' : ''}`}>
      <div className="flex items-center gap-2 text-tg-hint mb-1">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <div className="text-2xl font-bold text-tg-text">
        {isLoading ? (
          <div className="h-8 w-12 bg-tg-secondary-bg animate-pulse rounded" />
        ) : (
          value
        )}
      </div>
    </Card>
  )
}
interface QuickActionProps {
  icon: React.ReactNode
  label: string
  count?: number
  href: string
}

function QuickAction({ icon, label, count, href }: QuickActionProps) {
  return (
    <a
      href={href}
      className="flex items-center justify-between p-3 bg-tg-secondary-bg rounded-lg tap-highlight-transparent active:opacity-70 transition-opacity"
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm text-tg-text">{label}</span>
      </div>
      {count !== undefined && count > 0 && (
        <Badge variant="primary">{count}</Badge>
      )}
    </a>
  )
}
